<template>
  <div class="admin-pricing">
    <!-- 히어로 섹션 -->
    <section class="hero-section">
      <div class="hero-content">
        <div class="hero-badge">
          <v-icon size="16">mdi-star</v-icon>
          <span>요양기관 맞춤 솔루션</span>
        </div>
        <h1 class="hero-title">
          요양원 운영의 <span class="highlight">모든 것</span>을<br />
          하나로 통합하세요
        </h1>
        <p class="hero-description">
          임금관리부터 회계, 요양보호사 앱까지<br />
          복잡한 업무를 간편하게 관리하는 통합 솔루션
        </p>
      </div>
      <div class="hero-decoration">
        <div class="floating-card card-1">
          <v-icon color="success">mdi-check-circle</v-icon>
          <span>자동 급여계산</span>
        </div>
        <div class="floating-card card-2">
          <v-icon color="primary">mdi-cellphone</v-icon>
          <span>모바일 앱 지원</span>
        </div>
        <div class="floating-card card-3">
          <v-icon color="warning">mdi-chart-line</v-icon>
          <span>실시간 리포트</span>
        </div>
      </div>
    </section>

    <!-- 가격 정책 섹션 -->
    <section class="pricing-section">
      <div class="section-header">
        <h2 class="section-title">심플한 가격 정책</h2>
        <p class="section-subtitle">필요한 모든 기능을 합리적인 가격에</p>
      </div>

      <!-- 결제 주기 토글 -->
      <div class="billing-toggle">
        <span :class="{ active: billingCycle === 'monthly' }">월간 결제</span>
        <v-switch
          v-model="isAnnual"
          color="primary"
          hide-details
          density="compact"
          inset
        />
        <span :class="{ active: billingCycle === 'annual' }">
          연간 결제
          <v-chip color="success" size="x-small" variant="flat" class="ml-1">10% 할인</v-chip>
        </span>
      </div>

      <!-- 가격 카드 -->
      <div class="pricing-cards">
        <v-card class="pricing-card premium" elevation="8">
          <div class="card-ribbon">
            <span>BEST</span>
          </div>
          <div class="card-header">
            <div class="plan-name">프리미엄 플랜</div>
            <div class="plan-tagline">모든 기능을 제한 없이</div>
          </div>
          
          <div class="card-price">
            <div class="price-wrapper">
              <span class="currency">₩</span>
              <span class="amount">{{ formattedPrice }}</span>
              <span class="period">/월</span>
            </div>
            <div class="price-note" v-if="isAnnual">
              연간 결제 시 {{ formattedAnnualTotal }} (10% 할인 적용)
            </div>
            <div class="price-note" v-else>
              연간 결제 시 10% 할인 적용
            </div>
            <div class="tax-note">
              <v-icon size="14" class="mr-1">mdi-information-outline</v-icon>
              부가세 별도
            </div>
          </div>

          <v-divider class="my-4" />

          <div class="card-features">
            <div class="feature-group">
              <div class="feature-title">
                <v-icon color="primary" size="20">mdi-cash-register</v-icon>
                임금 관리
              </div>
              <ul class="feature-list">
                <li><v-icon color="success" size="16">mdi-check</v-icon> 자동 급여 계산</li>
                <li><v-icon color="success" size="16">mdi-check</v-icon> 4대보험 자동 산출</li>
                <li><v-icon color="success" size="16">mdi-check</v-icon> 급여명세서 자동 발송</li>
                <li><v-icon color="success" size="16">mdi-check</v-icon> 근로계약서 관리</li>
              </ul>
            </div>

            <div class="feature-group">
              <div class="feature-title">
                <v-icon color="primary" size="20">mdi-calculator-variant</v-icon>
                회계 관리
              </div>
              <ul class="feature-list">
                <li><v-icon color="success" size="16">mdi-check</v-icon> 입출금 내역 관리</li>
                <li><v-icon color="success" size="16">mdi-check</v-icon> 예산 편성 및 관리</li>
                <li><v-icon color="success" size="16">mdi-check</v-icon> 재무제표 자동 생성</li>
                <li><v-icon color="success" size="16">mdi-check</v-icon> 수입자료 분석</li>
              </ul>
            </div>

            <div class="feature-group">
              <div class="feature-title">
                <v-icon color="primary" size="20">mdi-account-heart</v-icon>
                요양보호사 앱
              </div>
              <ul class="feature-list">
                <li><v-icon color="success" size="16">mdi-check</v-icon> 근무 일정 확인</li>
                <li><v-icon color="success" size="16">mdi-check</v-icon> 급여명세서 조회</li>
                <li><v-icon color="success" size="16">mdi-check</v-icon> 공지사항 알림</li>
                <li><v-icon color="success" size="16">mdi-check</v-icon> 문서 열람</li>
              </ul>
            </div>

            <div class="feature-group">
              <div class="feature-title">
                <v-icon color="primary" size="20">mdi-account-child</v-icon>
                보호자 앱
              </div>
              <ul class="feature-list">
                <li><v-icon color="success" size="16">mdi-check</v-icon> 케어 일지 확인</li>
                <li><v-icon color="success" size="16">mdi-check</v-icon> 일정 알림 수신</li>
                <li><v-icon color="success" size="16">mdi-check</v-icon> 시설 공지 확인</li>
                <li><v-icon color="success" size="16">mdi-check</v-icon> 담당자 연락</li>
              </ul>
            </div>
          </div>

          <v-btn
            color="primary"
            size="large"
            block
            class="subscribe-btn"
            @click="handleSubscribe"
          >
            <v-icon start>mdi-rocket-launch</v-icon>
            지금 시작하기
          </v-btn>
        </v-card>
      </div>
    </section>

    <!-- 서비스 상세 섹션 -->
    <section class="services-section">
      <div class="section-header">
        <h2 class="section-title">포함된 서비스</h2>
        <p class="section-subtitle">요양원 운영에 필요한 모든 기능을 한곳에서</p>
      </div>

      <div class="services-grid">
        <div class="service-card" v-for="service in services" :key="service.title">
          <div class="service-icon" :style="{ background: service.gradient }">
            <v-icon :color="service.iconColor" size="32">{{ service.icon }}</v-icon>
          </div>
          <h3 class="service-title">{{ service.title }}</h3>
          <p class="service-description">{{ service.description }}</p>
          <ul class="service-features">
            <li v-for="feature in service.features" :key="feature">
              <v-icon color="success" size="14">mdi-check-circle</v-icon>
              {{ feature }}
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- FAQ 섹션 -->
    <section class="faq-section">
      <div class="section-header">
        <h2 class="section-title">자주 묻는 질문</h2>
      </div>

      <v-expansion-panels variant="accordion" class="faq-panels">
        <v-expansion-panel v-for="(faq, index) in faqs" :key="index">
          <v-expansion-panel-title>
            <v-icon color="primary" class="mr-3">mdi-help-circle</v-icon>
            {{ faq.question }}
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            {{ faq.answer }}
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </section>

    <!-- CTA 섹션 -->
    <section class="cta-section">
      <div class="cta-content">
        <h2 class="cta-title">지금 바로 시작하세요</h2>
        <p class="cta-description">
          복잡한 요양원 운영, CAION과 함께라면 쉬워집니다
        </p>
        <div class="cta-buttons">
          <v-btn color="white" size="large" variant="flat" class="cta-btn-primary" @click="handleSubscribe">
            <v-icon start>mdi-rocket-launch</v-icon>
            구독 시작하기
          </v-btn>
          <v-btn color="white" size="large" variant="outlined" class="cta-btn-secondary">
            <v-icon start>mdi-phone</v-icon>
            문의하기
          </v-btn>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

// 결제 주기 상태
const isAnnual = ref(false);
const billingCycle = computed(() => isAnnual.value ? 'annual' : 'monthly');

// 가격 계산
const monthlyPrice = 100000;
const annualDiscount = 0.1;

const formattedPrice = computed(() => {
  const price = isAnnual.value ? monthlyPrice * (1 - annualDiscount) : monthlyPrice;
  return price.toLocaleString('ko-KR');
});

const formattedAnnualTotal = computed(() => {
  const total = monthlyPrice * 12 * (1 - annualDiscount);
  return `₩${total.toLocaleString('ko-KR')}`;
});

// 서비스 정보
const services = ref([
  {
    title: '임금 관리',
    icon: 'mdi-cash-register',
    iconColor: '#fff',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    description: '복잡한 급여 계산을 자동화하여 정확하고 빠르게 처리합니다.',
    features: [
      '근무시간 기반 자동 급여계산',
      '4대보험 자동 공제 계산',
      '급여명세서 자동 생성 및 발송',
      '연차/휴가 관리'
    ]
  },
  {
    title: '회계 관리',
    icon: 'mdi-calculator-variant',
    iconColor: '#fff',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    description: '입출금부터 재무제표까지 체계적인 회계 관리를 지원합니다.',
    features: [
      '입출금 내역 자동 분류',
      '예산 편성 및 집행 관리',
      '재무제표 자동 생성',
      '월별/연별 리포트'
    ]
  },
  {
    title: '요양보호사 앱',
    icon: 'mdi-account-heart',
    iconColor: '#fff',
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    description: '요양보호사가 언제 어디서나 필요한 정보를 확인할 수 있습니다.',
    features: [
      '모바일 근무 일정 확인',
      '급여명세서 즉시 조회',
      '공지사항 푸시 알림',
      '근로계약서 열람'
    ]
  },
  {
    title: '보호자 앱',
    icon: 'mdi-account-child',
    iconColor: '#fff',
    gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    description: '보호자에게 투명한 케어 정보를 제공하여 신뢰를 높입니다.',
    features: [
      '실시간 케어 일지 확인',
      '일정 및 알림 수신',
      '시설 공지사항 확인',
      '담당자 직접 연락'
    ]
  }
]);

// FAQ
const faqs = ref([
  {
    question: '결제는 어떻게 진행되나요?',
    answer: '신용카드, 계좌이체, 자동이체 등 다양한 결제 방식을 지원합니다. 구독 시작 후 매월 동일한 날짜에 자동 결제가 이루어집니다.'
  },
  {
    question: '연간 결제의 할인은 어떻게 적용되나요?',
    answer: '연간 결제 선택 시 월 구독료의 10%가 할인되어, 월 90,000원(부가세 별도)으로 이용하실 수 있습니다. 연간 총 1,080,000원(부가세 별도)이 결제됩니다.'
  },
  {
    question: '구독 취소는 언제든 가능한가요?',
    answer: '네, 언제든지 구독을 취소하실 수 있습니다. 취소 후에도 결제 기간 만료일까지는 모든 기능을 정상적으로 이용하실 수 있습니다.'
  },
  {
    question: '추가 요금이 발생하나요?',
    answer: '기본 구독료 외에 추가 요금은 발생하지 않습니다. 요양보호사 앱, 보호자 앱 모두 기본 구독에 포함되어 있습니다.'
  },
  {
    question: '데이터 이전 서비스를 제공하나요?',
    answer: '기존 시스템에서 사용하던 데이터 이전을 무료로 지원해드립니다. 담당자가 직접 도와드리니 문의해 주세요.'
  }
]);

// 구독 버튼 핸들러
const handleSubscribe = () => {
  // 구독 프로세스 시작 (추후 구현)
  console.log('구독 시작:', billingCycle.value);
};
</script>

<style lang="scss" scoped>
@import '../../assets/styles/adminPricing.scss';
</style>
























